import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { BedDouble, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fmtIsk } from "@/lib/price";

// Owner-side sanity check: is this hotel actually available for the client's dates,
// and what is the cheapest real rate right now? Never touches the client's itinerary.
export const HotelRateCheck = ({ token, hotel, arrival, departure, pax, index }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [env, setEnv] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const checkin = hotel.check_in || arrival || "";
  const checkout = hotel.check_out || departure || "";

  const openModal = () => {
    setQuery(hotel.name || "");
    setMatches(null);
    setPicked(null);
    setResult(null);
    setOpen(true);
  };

  const search = async () => {
    if (query.trim().length < 2) {
      toast.error("Type at least 2 letters of the hotel name.");
      return;
    }
    setSearching(true);
    setMatches(null);
    setPicked(null);
    setResult(null);
    try {
      const res = await axios.get(`${API}/owner/hotel-search`, { headers, params: { q: query.trim() } });
      setMatches(res.data.hotels || []);
      setEnv(res.data.env || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reach the rate service.");
    } finally {
      setSearching(false);
    }
  };

  const checkRate = async (h) => {
    setPicked(h);
    setResult(null);
    if (!checkin || !checkout) {
      toast.error("Set the stay's in/out dates (or the journey dates) first.");
      return;
    }
    setChecking(true);
    try {
      const res = await axios.post(
        `${API}/owner/hotel-rates`,
        {
          hotel_id: h.hotel_id,
          checkin,
          checkout,
          adults: Math.min(Math.max(parseInt(pax, 10) || 2, 1), 20),
          rooms: Math.min(Math.max(parseInt(hotel.rooms, 10) || 1, 1), 10),
          currency: "ISK",
        },
        { headers }
      );
      setResult(res.data);
      setEnv(res.data.env || env);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch the live rate.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`trip-hotel-liverate-${index}`}
        onClick={openModal}
        title="Check live availability and the cheapest public rate for this stay"
        className="flex shrink-0 items-center gap-1.5 border border-sky-500/40 px-2.5 py-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-sky-200/90 transition-colors hover:bg-sky-500 hover:text-black"
      >
        <BedDouble className="h-3 w-3" /> Live rate
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="rate-check-modal"
            className="fixed inset-0 z-[100] overflow-y-auto bg-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="mx-auto max-w-lg px-4 py-10">
              <div className="border border-amber-500/20 bg-[#0c0c10]">
                <div className="flex items-center justify-between border-b border-amber-500/15 px-5 py-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Live rate check — {hotel.name || "this stay"}</p>
                    <p className="mt-1 text-[11px] text-neutral-500">
                      Only you see this — it never touches the itinerary.
                      {env === "sandbox" && (
                        <span data-testid="rate-env-badge" className="ml-2 border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[8px] uppercase tracking-[0.15em] text-sky-200">
                          Test data — sandbox
                        </span>
                      )}
                    </p>
                  </div>
                  <button data-testid="rate-check-close" onClick={() => setOpen(false)} className="text-neutral-400 transition-colors hover:text-white" aria-label="Close">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="rate-search-input"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && search()}
                      placeholder="Hotel name as the booking sites know it"
                      className="min-w-0 flex-1 border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-50 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                    />
                    <button
                      data-testid="rate-search-button"
                      onClick={search}
                      disabled={searching}
                      className="flex shrink-0 items-center gap-2 bg-amber-500 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
                    >
                      {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Find
                    </button>
                  </div>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-neutral-600">
                    {checkin && checkout ? `Dates: ${checkin} → ${checkout} · ${parseInt(hotel.rooms, 10) || 1} room(s) · ${parseInt(pax, 10) || 2} pax` : "Set in/out dates on the stay first"}
                  </p>

                  {matches !== null && !picked && (
                    <div className="mt-4 max-h-72 divide-y divide-neutral-800/70 overflow-y-auto border border-neutral-800">
                      {matches.length === 0 && (
                        <p data-testid="rate-search-empty" className="px-4 py-6 text-center text-sm text-neutral-600">
                          No match — try the hotel's full official name.
                        </p>
                      )}
                      {matches.map((m) => (
                        <button
                          key={m.hotel_id}
                          data-testid={`rate-hotel-option-${m.hotel_id}`}
                          onClick={() => checkRate(m)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-amber-500/10"
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-serif text-sm text-neutral-100">{m.name}</span>
                            <span className="block text-[9px] uppercase tracking-[0.18em] text-neutral-500">{m.city}{m.stars ? ` · ${m.stars}★` : ""}</span>
                          </span>
                          <span className="shrink-0 text-[9px] uppercase tracking-[0.15em] text-amber-300/70">Check rate →</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {checking && (
                    <div data-testid="rate-check-loading" className="flex items-center justify-center gap-2 py-8 text-xs uppercase tracking-[0.2em] text-neutral-500">
                      <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Asking the suppliers…
                    </div>
                  )}

                  {result && !checking && (
                    <div data-testid="rate-check-result" className="mt-4 border border-amber-500/25 bg-amber-500/5 p-4">
                      <p className="font-serif text-lg font-light text-neutral-50">{picked?.name}</p>
                      {result.available ? (
                        <>
                          <p className="mt-2 font-luxe text-2xl tracking-wide text-amber-200">
                            {fmtIsk(Math.round(result.cheapest.amount))}
                            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">cheapest · {result.nights} night{result.nights === 1 ? "" : "s"}</span>
                          </p>
                          <p className="mt-2 text-xs text-neutral-400">
                            {result.cheapest.room_name}
                            {result.cheapest.board_name ? ` · ${result.cheapest.board_name}` : ""}
                          </p>
                          <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em]">
                            {result.cheapest.refundable ? (
                              <span data-testid="rate-refundable" className="text-emerald-300/90">Refundable rate</span>
                            ) : (
                              <span data-testid="rate-nonrefundable" className="text-orange-300/90">Non-refundable</span>
                            )}
                          </p>
                        </>
                      ) : (
                        <p data-testid="rate-check-unavailable" className="mt-2 text-sm text-orange-200/90">
                          No availability found for these dates — the hotel may be full or not listed for this period.
                        </p>
                      )}
                      <button
                        data-testid="rate-check-again"
                        onClick={() => {
                          setPicked(null);
                          setResult(null);
                        }}
                        className="mt-4 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                      >
                        Pick a different hotel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
